import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, FolderInput, Play, ImageIcon } from "lucide-react";
import { api, API, errMsg } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useTitle } from "../lib/seo";

export default function Gallery() {
  useTitle("Mi galería · AR Studio Creative");
  const [data, setData] = useState({ items: [], folders: [] });
  const [folder, setFolder] = useState("");
  const [kind, setKind] = useState("");
  const [target, setTarget] = useState("General");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const fileRef = useRef(null);

  const [canUpload, setCanUpload] = useState(true);
  useEffect(() => { api.get("/account/usage").then((r) => setCanUpload(r.data.uploads)); }, []);
  const load = () => api.get("/gallery", { params: { folder: folder || undefined, kind: kind || undefined } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [folder, kind]); // eslint-disable-line

  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      try { const fd = new FormData(); fd.append("file", f); await api.post(`/gallery/upload?folder=${encodeURIComponent(target || "General")}`, fd); ok++; }
      catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    setBusy(false); e.target.value = "";
    if (ok) toast.success(`${ok} archivo(s) subido(s)`);
    load();
  };
  const move = async (g) => {
    const f = window.prompt("Mover a la carpeta:", g.folder);
    if (!f) return;
    await api.patch(`/gallery/${g.id}`, { folder: f }); toast.success(`Movido a ${f}`); load();
  };
  const del = async (g) => { if (!window.confirm(`¿Eliminar "${g.title}" de tu galería?`)) return; await api.delete(`/gallery/${g.id}`); toast.success("Eliminado"); load(); };

  const chip = (active) => `px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? "border-[#E8C47C] bg-[#E8C47C]/10 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`;

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="gallery-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex flex-wrap gap-4 items-end justify-between">
          <div><h1 className="text-3xl text-white">Mi galería</h1><p className="text-zinc-400 text-sm mt-1">Sube tus videos e imágenes y reutilízalos en cualquier escena.</p></div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input data-testid="gallery-folder-input" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Carpeta" className="bg-zinc-900/60 w-40" />
            <input ref={fileRef} type="file" multiple accept="image/*,video/*" hidden onChange={upload} data-testid="gallery-upload-input" />
            {!canUpload && <span className="text-xs text-amber-300/80" data-testid="gallery-plan-note">Disponible en planes de pago</span>}
            <Button data-testid="gallery-upload-btn" disabled={busy || !canUpload} onClick={() => fileRef.current.click()} className="rounded-full bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" />Subir archivos</>}
            </Button>
          </div>
        </div>
        <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
          <button className={chip(!folder)} onClick={() => setFolder("")} data-testid="gallery-folder-all">Todas</button>
          {data.folders.map((f) => <button key={f} className={chip(folder === f)} onClick={() => setFolder(f)} data-testid={`gallery-folder-${f}`}>{f}</button>)}
          <span className="w-px bg-white/10 mx-2" />
          {[["", "Todo"], ["video", "Videos"], ["photo", "Imágenes"]].map(([k, l]) => <button key={l} className={chip(kind === k)} onClick={() => setKind(k)} data-testid={`gallery-kind-${k || "all"}`}>{l}</button>)}
        </div>
        {data.items.length === 0 ? (
          <div className="panel mt-8 p-16 text-center text-zinc-500" data-testid="gallery-empty"><ImageIcon className="h-8 w-8 mx-auto mb-3 text-zinc-600" />Tu galería está vacía.</div>
        ) : (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {data.items.map((g) => (
              <div key={g.id} className="panel overflow-hidden group" data-testid={`gallery-item-${g.id}`}>
                <button onClick={() => setView(g)} className="relative block w-full aspect-video bg-zinc-900 overflow-hidden">
                  <img src={`${API}/gallery/${g.id}/thumb`} alt={g.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {g.kind === "video" && <span className="absolute bottom-1.5 right-1.5 text-[10px] font-mono-tc bg-black/70 px-1.5 rounded flex items-center gap-1"><Play className="h-2.5 w-2.5" />{Math.round(g.duration || 0)}s</span>}
                </button>
                <div className="p-3">
                  <p className="text-xs text-white truncate">{g.title}</p>
                  <div className="flex items-center justify-between mt-1"><span className="text-[10px] text-zinc-500">{g.folder}</span>
                    <div className="flex">
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`gallery-move-${g.id}`} onClick={() => move(g)}><FolderInput className="h-3.5 w-3.5 text-zinc-500" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`gallery-delete-${g.id}`} onClick={() => del(g)}><Trash2 className="h-3.5 w-3.5 text-zinc-500" /></Button>
                    </div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-3xl" data-testid="gallery-viewer">
          <DialogHeader><DialogTitle className="text-white">{view?.title}</DialogTitle></DialogHeader>
          {view && (view.kind === "video" ? <video src={`${API}/gallery/${view.id}/file`} controls autoPlay className="w-full rounded-xl bg-black max-h-[70vh]" />
            : <img src={`${API}/gallery/${view.id}/file`} alt={view.title} className="w-full rounded-xl max-h-[70vh] object-contain" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
